/**
 * Voice player — queued playback of pre-recorded sample ids.
 *
 *   • Each speak() enqueues one message (a list of sample ids).
 *   • 500 ms gap between consecutive messages.
 *   • `intraGapMs` (default 0) between ids *within* one message;
 *     ≈100 ms for distance+direction composites.
 *
 * Each id maps to `voice/<id>.mp3`, resolved bundled-first then server-fallback
 * (see ../asset.ts) — so the server can add new voice lines without a client
 * release. As each id plays, its display text (from the bundled definitions,
 * with a one-time server merge for unknown ids) is reported via [onText] for
 * the on-screen log.
 */
import { audioContext, loadAudio, masterOut, voicePath } from './context';
import { textFor, ensureServerDefinitions } from './definitions';

const INTER_MESSAGE_GAP_MS = 500;
/** Max messages allowed to wait behind the one currently playing. */
const MAX_QUEUED = 2;

interface Msg {
  tokens: string[];
  intraGapMs: number;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export class VoicePlayer {
  private queue: Msg[] = [];
  private draining = false;
  private gain: GainNode;
  /** While held, the drain loop parks instead of playing — clips queue up. Used
   *  to suspend playback while the push-to-talk SCO link is up. */
  private held = false;
  private holdWaiters: (() => void)[] = [];

  /** Called with the resolved display text for each message (for the log). */
  onText?: (text: string) => void;

  /**
   * Called with `true` when playback of the queue begins and `false` once it
   * drains. Used to mute speech recognition while we're talking over the
   * speaker (so our own samples don't trigger commands).
   */
  onActiveChange?: (active: boolean) => void;

  constructor() {
    const ctx = audioContext();
    this.gain = ctx.createGain();
    this.gain.gain.value = 1;
    this.gain.connect(masterOut());
  }

  speak(tokens: string[], intraGapMs = 0): void {
    if (!tokens.length) return;
    this.queue.push({ tokens, intraGapMs });
    // Cap the backlog so playback can't fall far behind live events: if more
    // than MAX_QUEUED messages are waiting (beyond the one currently playing),
    // drop the oldest until 2 remain. The on-screen log still shows every
    // message — reportText() records them regardless of what we drop here.
    if (this.queue.length > MAX_QUEUED) {
      this.queue.splice(0, this.queue.length - MAX_QUEUED);
    }
    void this.reportText(tokens);
    void this.drain();
  }

  /** Drop anything pending (e.g. on game end / audio focus loss). */
  clear(): void {
    this.queue.length = 0;
  }

  /** Hold (true) parks the queue without playing; release (false) resumes and
   *  flushes it. Used to suspend voice playback while the push-to-talk SCO link is
   *  up, so a reply plays over A2DP afterwards rather than being cut by the switch. */
  setHold(hold: boolean): void {
    if (hold === this.held) return;
    this.held = hold;
    if (!hold) {
      const waiters = this.holdWaiters;
      this.holdWaiters = [];
      for (const w of waiters) w();
    }
  }

  private waitUnhold(): Promise<void> {
    if (!this.held) return Promise.resolve();
    return new Promise((res) => this.holdWaiters.push(res));
  }

  /** Resolve a caption for the message, fetching server definitions if needed. */
  private async reportText(tokens: string[]): Promise<void> {
    if (!this.onText) return;
    if (tokens.some((t) => textFor(t) === undefined)) {
      await ensureServerDefinitions();
    }
    const text = tokens.map((t) => textFor(t) ?? t).join(' ');
    this.onText(text);
  }

  private async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    // Announce "active" lazily, only once we actually start playing — so a clip
    // that merely queues behind a hold doesn't mute the recogniser meanwhile.
    let announced = false;
    try {
      let first = true;
      while (this.queue.length) {
        await this.waitUnhold();
        if (!announced) {
          announced = true;
          this.onActiveChange?.(true);
        }
        const msg = this.queue.shift()!;
        if (!first) await sleep(INTER_MESSAGE_GAP_MS);
        first = false;
        for (let i = 0; i < msg.tokens.length; i++) {
          if (i > 0 && msg.intraGapMs > 0) await sleep(msg.intraGapMs);
          await this.playToken(msg.tokens[i]);
        }
      }
    } finally {
      this.draining = false;
      if (announced) this.onActiveChange?.(false);
    }
  }

  private async playToken(token: string): Promise<void> {
    const buf = await loadAudio(voicePath(token));
    if (!buf) {
      console.warn('[voice] missing sample:', token);
      return;
    }
    await this.playBuffer(buf);
  }

  private playBuffer(buf: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      const ctx = audioContext();
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gain);
      src.onended = () => resolve();
      src.start();
    });
  }
}

export const voice = new VoicePlayer();
